import type { Partner } from '$lib/types/Partner';
import { HttpService } from './HttpService';
import { SPBError } from './SPBError';

/**
 * Handle Partner data retrieval and management with Backend and Middleware
 */
export class PartnerService extends HttpService {
    private numPartners: number = 0;

    constructor() {
        super();
    }

    /**
     * Get Partners of this registry, based on page number and filters applicable on Partners
     */
    async getPartners() {
        return await this.obtainPartners();
    }

    /**
     * Retrieve partner given partnerId
     * @param partnerId partner ID used to recognize partner
     * @returns
     */
    async getPartnerById(partnerId: string) {
        return (await this.obtainPartners()).find((partner) => partner.id === partnerId);
    }

    getTotalPartners(): number {
        return this.numPartners;
    }
    /**
     * Retrieve partner given partnerName
     * @param partnerId partner ID used to recognize partner
     * @returns
     */
    async getPartnerByName(partnerName: string) {
        return (await this.obtainPartners()).find((partner) => partner.name === partnerName);
    }

    async getPartnerByUrl(partnerUrl: string) {
        try {
            return (
                await this.get<Partner>(`${this.endPointUrl}/api/partner/info`, {
                    params: {
                        partner: partnerUrl,
                    },
                })
            ).data.response;
        } catch (error: any) {
            throw new SPBError(error?.response?.data?.err?.message);
        }
    }

    /**
     * Register new partner in the platform
     * @param partner partner object
     * @returns
     */
    async register(partner: any) {
        try {
            delete partner.id;
            await this.post(`${this.endPointUrl}/api/partner/create`, partner);
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    /**
     * Update partner in the SPB Platform
     * @param partner partner object to be updated
     * @returns
     */
    async update(partner: Partner) {
        try {
            await this.post(`${this.endPointUrl}/api/partner/update/${partner.id}`, partner);
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }
    /**
     * Obtain Partners available on SPB portal
     */
    private async obtainPartners(): Promise<Partner[]> {
        try {
            return (await this.get<Partner[]>(`${this.endPointUrl}/api/partner/list`, {
                params: {
                    limit: 100
                },
            })).data
                .response;
        } catch (error) {
            return [];
        }
    }

    async getDefaultFooter() {
        try {
            return await this.get(`${this.endPointUrl}/api/footer`);
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }
    async setDefaultFooter(text: string) {
        try {
            return await this.post(`${this.endPointUrl}/api/footer`, {text});
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }
}
